import { useEffect, useRef } from "react";
import { Film } from "lucide-react";
import { CRAFT_VIDEOS } from "../../lib/images";

function CraftClip({ src, caption, idx }) {
  const ref = useRef(null);

  useEffect(() => {
    const v = ref.current;
    if (!v || !src) return;
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();
    const io = new IntersectionObserver(
      ([entry]) => (entry.isIntersecting ? tryPlay() : v.pause()),
      { threshold: 0.2 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, [src]);

  const hasSrc = Boolean(src);

  return (
    <figure
      data-testid={`craft-clip-${idx}`}
      data-has-src={hasSrc ? "true" : "false"}
      className="relative group overflow-hidden bg-maroon-deep border border-[hsl(var(--gold)/0.25)]"
    >
      {hasSrc ? (
        <video
          ref={ref}
          muted
          loop
          autoPlay
          playsInline
          preload="auto"
          className="w-full h-[180px] md:h-[220px] object-cover"
          aria-label={caption}
        >
          <source src={src.replace(/\.mp4$/, ".webm")} type="video/webm" />
          <source src={src} type="video/mp4" />
        </video>
      ) : (
        <div
          aria-label={`${caption} — studio footage coming soon`}
          className="relative w-full h-[180px] md:h-[220px] flex flex-col items-center justify-center text-center px-4 overflow-hidden"
          style={{
            backgroundImage:
              "repeating-linear-gradient(45deg, hsl(var(--maroon)) 0px, hsl(var(--maroon)) 12px, hsl(var(--maroon-deep)) 12px, hsl(var(--maroon-deep)) 24px)",
          }}
        >
          <span className="absolute inset-0 bg-maroon-deep/70" aria-hidden="true" />
          <Film
            className="relative z-10 h-6 w-6 text-gold/80 mb-3"
            strokeWidth={1.2}
          />
          <p className="relative z-10 font-display italic text-ivory/90 text-sm">
            Studio footage
          </p>
          <p className="relative z-10 text-[10px] tracking-[0.32em] uppercase text-gold/80 mt-1">
            Coming soon
          </p>
        </div>
      )}
      <span className="absolute top-3 left-3 text-[10px] tracking-[0.25em] uppercase text-ivory/85 bg-maroon-deep/70 backdrop-blur-sm px-2.5 py-1">
        {caption}
      </span>
      <span className="absolute bottom-3 right-3 font-display italic text-gold text-xs">
        0{idx + 1}
      </span>
    </figure>
  );
}

function CraftBlock({ id, label, title, desc, image, clips, reverse = false }) {
  return (
    <div
      data-testid={`craft-block-${id}`}
      className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start reveal ${reverse ? "md:[&>:first-child]:order-2" : ""}`}
    >
      <div className="md:col-span-5">
        <div className="relative">
          <span
            aria-hidden="true"
            className="absolute -top-4 -left-4 md:-top-6 md:-left-6 w-full h-full border border-gold/60"
          />
          <img
            src={image}
            alt={`The finished ${label.toLowerCase()}`}
            loading="lazy"
            className="relative w-full h-[480px] md:h-[640px] object-cover"
          />
          <p className="mt-4 text-overline text-ivory/55">
            Plate · The Finished {label}
          </p>
        </div>
      </div>

      <div className="md:col-span-7">
        <p className="text-overline text-gold mb-4">{label}</p>
        <h3 className="font-display text-3xl md:text-4xl lg:text-[42px] leading-[1.1] tracking-tight">
          {title}
        </h3>
        <p className="mt-5 text-ivory/70 text-[15px] leading-relaxed max-w-xl">
          {desc}
        </p>

        <div className="mt-8 grid grid-cols-2 gap-3 md:gap-4">
          {clips.map((c, i) => (
            <CraftClip key={i} src={c.src} caption={c.caption} idx={i} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Craftsmanship() {
  return (
    <section
      id="craft"
      data-testid="craft-section"
      className="bg-maroon-deep text-ivory py-24 md:py-32 grain"
    >
      <div className="relative max-w-[1240px] mx-auto px-6 md:px-12">
        <div className="text-center mb-16 md:mb-20 reveal">
          <p className="text-overline text-gold mb-5">Behind The Brush</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            The craft,
            <br />
            <span className="italic text-[hsl(var(--gold-soft))]">in motion.</span>
          </h2>
          <p className="mt-6 max-w-xl mx-auto text-ivory/70 text-[15px] leading-relaxed">
            Every ARVI piece passes through hours of patient brushwork.
            Watch the studio at work — each motion you see here happens
            on the saree you eventually wear.
          </p>
        </div>

        <div className="space-y-24 md:space-y-32">
          <CraftBlock
            id="saree"
            label="Hand-Painted Saree"
            title={
              <>
                A saree, painted
                <br />
                <span className="italic">stroke by stroke.</span>
              </>
            }
            desc="From the first wash of colour to the final detail, the saree below was painted entirely by hand. The clips beside it are real moments from her studio — outlining motifs, layering pigment, and signing the final piece."
            image={CRAFT_VIDEOS.saree.finalImage}
            clips={CRAFT_VIDEOS.saree.clips}
          />
          <CraftBlock
            id="suit"
            label="Hand-Painted Suit Set"
            title={
              <>
                A suit, considered
                <br />
                <span className="italic">motif by motif.</span>
              </>
            }
            desc="Suit sets are painted on lighter, breathable fabrics — but the process is identical. Mixing pigment, drawing the border, layering the floral repeats, and finally placing the signature ornament."
            image={CRAFT_VIDEOS.suit.finalImage}
            clips={CRAFT_VIDEOS.suit.clips}
            reverse
          />
        </div>
      </div>
    </section>
  );
}
